/** Kategórie platených operácií — poradie = poradie sekcií v UI. */
export const KATEGORIE_OPERACII: { kod: string; label: string }[] = [
  { kod: "striekanie", label: "Striekanie a lakovanie" },
  { kod: "olejovanie", label: "Olejovanie" },
  { kod: "lepenie", label: "Lepenie a lisovanie" },
  { kod: "drazky_frezovanie", label: "Drážky a frézovanie" },
  { kod: "porez_olepovanie", label: "Porez a olepovanie" },
  { kod: "brusenie_povrch", label: "Brúsenie a povrch" },
  { kod: "calunenie", label: "Čalúnenie" },
  { kod: "praca_montaz", label: "Práca a montáž" },
];

/** Zoskupí operácie podľa kategórie v poradí KATEGORIE_OPERACII;
 *  neznáme/NULL kategórie idú na koniec ako „Nezaradené". */
export function zoskupOperacie<T extends { kategoria?: string | null }>(
  operacie: T[],
): { kod: string | null; label: string; items: T[] }[] {
  const skupiny: { kod: string | null; label: string; items: T[] }[] = [];
  for (const kat of KATEGORIE_OPERACII) {
    const items = operacie.filter((o) => o.kategoria === kat.kod);
    if (items.length) skupiny.push({ kod: kat.kod, label: kat.label, items });
  }
  const zname = new Set(KATEGORIE_OPERACII.map((k) => k.kod));
  const ostatne = operacie.filter((o) => !o.kategoria || !zname.has(o.kategoria));
  if (ostatne.length) {
    skupiny.push({ kod: null, label: "Nezaradené", items: ostatne });
  }
  return skupiny;
}

/**
 * Je to **výrobný postup** (KOLO 108)?
 *
 * Postup je **operácia s číslom** — to, čo si user napíše v Nastaveniach
 * a v tabuľke už len vyberá.
 *
 * Do KOLA 109 sa ponúkali aj systémové bez čísla, lebo neočíslovaný
 * `olep_nc` by inak z rozbaľovačky vypadol. Odkedy má číslo 1, je tá
 * vetva zbytočná — a bola to presne ona, čo držala v zozname `hw`
 * a `nc_lep_sucho`, ktoré tam user mať nechce. Riadky v DB zostávajú
 * (`hw` chodí z exportu), len sa neponúkajú.
 *
 * Predikát je **jeden pre Nastavenia aj pre tabuľku** — dve kópie by sa
 * rozišli a rozbaľovačka by ponúkala iné postupy, než sa dajú napísať.
 * (V KOLE 108 to tak nebolo: `OperacieSection` si predikát opísal
 * a zúženie tu by na Nastavenia vôbec nedosiahlo.)
 */
export function jePostup<T extends {
  postup_poradie?: number | null;
}>(op: T): boolean {
  return op.postup_poradie != null;
}

/** „2 · nc_olep" — číslo je poradie, ktoré si user určil v Nastaveniach. */
export function postupPopis<T extends {
  kod: string;
  postup_poradie?: number | null;
}>(op: T): string {
  return op.postup_poradie != null ? `${op.postup_poradie} · ${op.kod}` : op.kod;
}

/**
 * Výrobný postup, ktorým je označená **lepená vrstva sendviča**.
 *
 * Nie je to len hodnota postupu — je to **štrukturálna značka**. Dvojica
 * „dieťa + tento postup" je jediné, podľa čoho appka pozná vrstvu, a visí
 * na nej, že rodič sendviča neplatí dosku, že sa nereže na tabuli a koľko
 * je lepených škár. Zrkadlo backendovej konštanty v
 * `backend/src/tandem_budget/services/lepene_dielce.py`; paritu stráži
 * test, lebo TS a Python si reťazec zdieľať nevedia.
 *
 * Do KOLA 109 to bol `"lep_nc"` rozsypaný na ôsmich miestach vo FE.
 */
export const OPERACIA_LEPENA_VRSTVA = "lep_nc_olep";

/** Predvolený postup rodiča sendviča — sendvič sa najprv zlepí (KOLO 138). */
export const POSTUP_SENDVICA = OPERACIA_LEPENA_VRSTVA;

/**
 * Postup začína lepením (`lep_nc_olep`, `lep_nc_olep_nc`) — KOLO 138.
 *
 * User: *„keď zvolíš hrúbku 36, tak automaticky zvoľ lep_nc_olep a nedovoľ
 * mi zmeniť na olep_nc — vždy 36 sa musí lepiť"*. Rodič sendviča ponúka
 * len tieto postupy; backend iný odmietne (422). Zrkadlo
 * `lepene_dielce.je_lepiaci_postup`.
 */
export function jeLepiaciPostup(kod: string | null | undefined): boolean {
  return !!kod && kod.startsWith("lep_");
}

/**
 * Operácia, ktorou je označený **paušál zostavy** („Ostatné" — spojovací
 * materiál, `services/ostatne.py`).
 *
 * Je to **jediná operácia, ktorá smie visieť priamo na zostave**, teda
 * bez rodiča. Všetko ostatné operačné zakladá `PridatOperaciuDialog` ako
 * dieťa dielca. Bez tohto rozlíšenia platilo „bez rodiča a bez materiálu
 * = operácia", čo trafilo aj bežný dielec, ktorému user ešte nevybral
 * materiál: zošedivel, prišiel o bunku materiálu aj o plný detail a
 * nedal sa opraviť inak než zmazaním (KOLO 110).
 *
 * Zrkadlo konštanty v `backend/src/tandem_budget/services/ostatne.py`;
 * paritu stráži test, lebo TS a Python si reťazec zdieľať nevedia.
 */
export const OPERACIA_PAUSAL_ZOSTAVY = "ostatne";

/**
 * Operácia, ktorou sa platí **zaoblenie strán masívu** (KOLO 168).
 *
 * Masív sa ABS hranou neolepuje; zaoblenú stranu (agregát olepovačky)
 * appka účtuje sama za bežný meter — cenu mení user v Cenník → Operácie.
 * Preto ju „Pridať operáciu" neponúka (zaúčtovala by sa dvakrát)
 * a backend ju nedovolí zmazať. Zrkadlo `OPERACIA_ZAOBLENIE`
 * v `backend/src/tandem_budget/services/masiv.py`; paritu stráži test.
 */
export const OPERACIA_ZAOBLENIE = "zaoblenie";

/**
 * Minimum, ktoré predikáty nižšie čítajú.
 *
 * Štrukturálny tvar, nie `Dielec` z `@/api/schemas` — tento modul
 * zostáva bez závislosti na generovaných typoch (rovnako ako `jePostup`
 * a `postupPopis` vyššie), takže ho môže volať aj test s holým objektom.
 */
interface RiadokTvar {
  material_slot_id?: string | null;
  material_id_direct?: string | null;
  parent_dielec_id?: string | null;
  operacia?: string | null;
}

/** Riadok nesie LEN operáciu: žiadny materiál a nie je to lepená vrstva. */
function lenOperacia(v: RiadokTvar): boolean {
  return (
    !v.material_slot_id &&
    !v.material_id_direct &&
    !!v.operacia &&
    v.operacia !== OPERACIA_LEPENA_VRSTVA
  );
}

/**
 * Vnorený operačný riadok — dieťa bez materiálu, len s platenou
 * operáciou (množstvo sa berie z rozmerov rodiča).
 *
 * **Toto je primitív.** Je to znak k znaku to isté, čo
 * `recalculator._je_operacia_row`, a rodič v podmienke nie je kozmetika:
 * je to jediné, odkiaľ sa dá vziať plocha/dĺžka pre `m2`/`m` operáciu
 * (`_load_dielec_context` kopíruje `x_mm`/`y_mm` práve z neho).
 * Paritu stráži test, ktorý číta tento zdroják.
 */
export function jeOperaciaChild(v: RiadokTvar): boolean {
  return v.parent_dielec_id != null && lenOperacia(v);
}

/**
 * Paušál zostavy — účtuje sa za kus, rodiča nemá a mať nemôže.
 *
 * `lenOperacia` tu prispieva už len kontrolou materiálu (porovnanie
 * s kódom paušálu zvyšok subsumuje), ale zostáva: „riadok nesie len
 * operáciu" má byť definované na jednom mieste.
 */
export function jePausalZostavy(v: RiadokTvar): boolean {
  return (
    v.parent_dielec_id == null &&
    v.operacia === OPERACIA_PAUSAL_ZOSTAVY &&
    lenOperacia(v)
  );
}

/**
 * Kreslí sa riadok zredukovane (`OperaciaRow` / `OperaciaDetailBody`)?
 *
 * Únia **dvoch pomenovaných tvarov**, nie „všetko bez materiálu".
 *
 * Do KOLA 110 tu podmienka rodiča nebola vôbec — KOLO 55 ju zámerne
 * pustilo, aby sa paušál „Ostatné" nekreslil ako plnohodnotný dielec.
 * Odkedy je Výrobný postup `<select>` na každom riadku (KOLO 108),
 * spadol sem **každý bežný dielec, ktorému user ešte nevybral materiál**:
 * zošedol, stratil bunky Materiál/Hrany/Prírez, vypadol z hromadnej
 * úpravy a detail mu ponúkol len Ks a Zmazať — z čoho neviedla cesta
 * späť inak než zmazaním. Trafilo to aj dielce z importu, ktorým sa
 * materiál nenamapoval, teda presne tie, kvôli ktorým sa do zákazky
 * ide pozrieť.
 *
 * Výnimka preto musí byť **pomenovaná**, nie odvodená z absencie.
 */
export function jeOperaciaRiadok(v: RiadokTvar): boolean {
  return jeOperaciaChild(v) || jePausalZostavy(v);
}

/** Operácia laminátu pod doskou (KOLO 234, `services/laminaty_dielca`). */
export const OPERACIA_LAMINOVANIE = "laminovanie";

/** Riadok laminátu pod doskou — dieťa s materiálom Laminát (KOLO 234). */
export function jeLaminatRiadok(d: {
  parent_dielec_id?: string | null;
  operacia?: string | null;
}): boolean {
  return d.parent_dielec_id != null && d.operacia === OPERACIA_LAMINOVANIE;
}

